import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import useAuth from '../hooks/useAuth'
import { AIAgentContext } from './aiAgentContext'
import {
  registerAssistantAccess,
  verifyAssistantPin,
  type AssistantAccessGrant
} from '../services/assistantService'

function AIAgentSessionProvider({
  userId,
  children
}: {
  userId: number | null
  children: ReactNode
}) {
  const [grant, setGrant] = useState<AssistantAccessGrant | null>(null)
  const [isUnlocking, setIsUnlocking] = useState(false)
  const grantRef = useRef<AssistantAccessGrant | null>(null)
  const requestRef = useRef<AbortController | null>(null)
  const mountedRef = useRef(true)

  const lockPin = useCallback(() => {
    grantRef.current = null

    if (mountedRef.current) {
      setGrant(null)
    }
  }, [])

  useEffect(() => {
    mountedRef.current = true
    const unregister = registerAssistantAccess(() => {
      const currentGrant = grantRef.current
      return currentGrant && currentGrant.expiresAt > Date.now() ? currentGrant.accessToken : null
    }, lockPin)

    return () => {
      mountedRef.current = false
      grantRef.current = null
      requestRef.current?.abort()
      unregister()
    }
  }, [lockPin])

  useEffect(() => {
    if (!grant) {
      return
    }

    const timer = setTimeout(lockPin, Math.max(0, grant.expiresAt - Date.now()))
    return () => clearTimeout(timer)
  }, [grant, lockPin])

  const unlockPin = useCallback(
    async (pinCode: string): Promise<boolean> => {
      if (userId === null || requestRef.current || !pinCode.trim()) {
        return false
      }

      const controller = new AbortController()
      requestRef.current = controller
      setIsUnlocking(true)

      try {
        const newGrant = await verifyAssistantPin(pinCode.trim(), controller.signal)

        if (controller.signal.aborted) {
          return false
        }

        if (
          !/^[a-f0-9]{64}$/.test(newGrant.accessToken) ||
          !Number.isFinite(newGrant.expiresAt) ||
          newGrant.expiresAt <= Date.now()
        ) {
          throw new Error('Invalid AI agent access response.')
        }

        grantRef.current = newGrant
        setGrant(newGrant)
        return true
      } catch (error) {
        if (controller.signal.aborted) {
          return false
        }

        if (
          axios.isAxiosError(error) &&
          error.response?.status === 403 &&
          error.response.data?.error === 'AI_AGENT_PIN_INVALID'
        ) {
          return false
        }

        throw error
      } finally {
        requestRef.current = null

        if (mountedRef.current) {
          setIsUnlocking(false)
        }
      }
    },
    [userId]
  )

  const value = useMemo(
    () => ({ isPinUnlocked: grant !== null, isUnlocking, unlockPin }),
    [grant, isUnlocking, unlockPin]
  )

  return <AIAgentContext.Provider value={value}>{children}</AIAgentContext.Provider>
}

export function AIAgentProvider({ children }: { children: ReactNode }) {
  const { currentUser, isAuthenticated } = useAuth()
  const userId = isAuthenticated ? (currentUser?.id ?? null) : null

  // A different authenticated identity gets fresh state, including after sign-out/sign-in.
  return (
    <AIAgentSessionProvider key={userId ?? 'signed-out'} userId={userId}>
      {children}
    </AIAgentSessionProvider>
  )
}
